import camelCase from 'camelcase';

export const createItemKey = params => (typeof params === 'string' ? params : params ? Object.entries(params).toString() : null); // eslint-disable-line

const initializeState = entities => {
    const state = {
        loading: {},
        busy: {},
        errors: {},
        busyItem: {},
        deletedItem: {},
        updatedItem: {},
    };

    Object.keys(entities).forEach(name => {
        const camelName = camelCase(name);
        state[camelName] = null;
        state.loading[camelName] = false;
        state.busy[camelName] = false;
        state.errors[camelName] = null;
        state.busyItem[camelName] = {};
        state.deletedItem[camelName] = {};
        state.updatedItem[camelName] = {};
    });

    return state;
};

const initializeGetters = entities => {
    const getters = {};

    Object.keys(entities).forEach(name => {
        const camelName = camelCase(name);
        const pascalName = camelCase(name, { pascalCase: true });
        const defaultValue = entities[name].default !== undefined ? entities[name].default : {};

        getters[`get${pascalName}`] = state => state[camelName] || defaultValue;
    });

    return {
        ...getters,
        getLoading: state => type => state.loading[type],
        getBusy: state => type => state.busy[type],
        getError: state => type => state.errors[type],
        getBusyItem: state => (type, params) => {
            const itemKey = createItemKey(params);
            return itemKey ? state.busyItem[type][itemKey] : false;
        },
        getDeletedItem: state => (type, params) => {
            const itemKey = createItemKey(params);
            return itemKey ? state.deletedItem[type][itemKey] : false;
        },
        getUpdatedItem: state => (type, params) => {
            const itemKey = createItemKey(params);
            return itemKey ? state.updatedItem[type][itemKey] : false;
        },
    };
};

const initializeMutations = entities => {
    const mutations = {};

    Object.keys(entities).forEach(name => {
        const camelName = camelCase(name);
        const pascalName = camelCase(name, { pascalCase: true });
        const defaultValue = entities[name].default !== undefined ? entities[name].default : {};

        mutations[`set${pascalName}`] = (state, payload) => {
            state[camelName] = payload;
        };
        mutations[`clear${pascalName}`] = state => {
            state[camelName] = defaultValue;
        };
    });

    return {
        ...mutations,
        setLoading: (state, { key, value }) => {
            state.loading[key] = !!value;
        },
        setBusy: (state, { key, value }) => {
            state.busy[key] = !!value;
        },
        setError: (state, { key, value }) => {
            state.errors[key] = value;
        },
        setBusyItem: (state, { key, itemKey, value }) => {
            const createdItemKey = createItemKey(itemKey);
            if (createdItemKey) {
                if (value) {
                    state.busyItem[key] = { ...state.busyItem[key], [createdItemKey]: !!value };
                } else {
                    delete state.busyItem[key][createdItemKey];
                    state.busyItem[key] = { ...state.busyItem[key] };
                }
            }
        },
        setDeletedItem: (state, { key, itemKey, value }) => {
            const createdItemKey = createItemKey(itemKey);
            if (createdItemKey) {
                if (value) {
                    state.deletedItem[key] = { ...state.deletedItem[key], [createdItemKey]: !!value };
                } else {
                    delete state.deletedItem[key][createdItemKey];
                    state.deletedItem[key] = { ...state.deletedItem[key] };
                }
            }
        },
        setUpdatedItem: (state, { key, itemKey, value }) => {
            const createdItemKey = createItemKey(itemKey);
            if (createdItemKey) {
                if (value) {
                    state.updatedItem[key] = { ...state.updatedItem[key], [createdItemKey]: !!value };
                } else {
                    delete state.updatedItem[key][createdItemKey];
                    state.updatedItem[key] = { ...state.updatedItem[key] };
                }
            }
        },
    };
};

const initializeActions = (entities, service) => {
    const actions = {};

    const createAction = (method, context, payload, name, camelName, pascalName, itemKey) => async () => {
        let data = entities[name].default !== undefined ? entities[name].default : {};
        context.commit('setLoading', { key: camelName, value: true });
        context.commit('setBusy', { key: camelName, value: true });
        context.commit('setBusyItem', { key: camelName, itemKey, value: true });
        context.commit('setError', { key: camelName, value: null });
        let response = null;
        try {
            data = await service[`${method}${pascalName}`](payload);
            context.commit(`set${pascalName}`, data);
            response = data;
        } catch (error) {
            context.commit(`clear${pascalName}`);
            context.commit('setError', { key: camelName, value: { ...error, message: error.message, requestType: method } });
            response = error;
        }
        context.commit('setLoading', { key: camelName, value: false });
        context.commit('setBusy', { key: camelName, value: false });
        context.commit('setBusyItem', { key: camelName, itemKey, value: false });
        if (response instanceof Error) throw response;
        return response;
    };

    Object.entries(entities).forEach(([name, { methods }]) => {
        const camelName = camelCase(name);
        const pascalName = camelCase(name, { pascalCase: true });

        methods.forEach(method => {
            actions[`${method}${pascalName}`] = (context, payload) => {
                const itemKey = createItemKey(payload?.urlParams);
                return createAction(method, context, payload, name, camelName, pascalName, itemKey)();
            };

            // Dodatkowy przypadek dla 'fetch', który działa tak samo jak 'get'
            if (method === 'get') {
                actions[`fetch${pascalName}`] = (context, payload) => context.dispatch(`get${pascalName}`, payload);
            }
        });
    });

    return actions;
};

export default {
    build(entities, service) {
        const state = initializeState(entities);
        const getters = initializeGetters(entities);
        const mutations = initializeMutations(entities);
        const actions = initializeActions(entities, service);

        return {
            state,
            getters,
            mutations,
            actions,
        };
    },
};
